import * as Yup from 'yup';
import { m } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import { contactApi } from 'src/api/commonapi';

import { useSnackbar } from 'src/components/snackbar';
import { varFade, MotionViewport } from 'src/components/animate';
import FormProvider, { RHFTextField } from 'src/components/hook-form';

// ----------------------------------------------------------------------

export default function ContactForm() {
  const { enqueueSnackbar } = useSnackbar();

  const ContactSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    subject: Yup.string().required('Subject is required'),
    message: Yup.string().required('Message is required'),
  });
  const defaultValues = {
    name: '',
    email: '',
    subject: '',
    message: '',
  };
  const methods = useForm({
    resolver: yupResolver(ContactSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const response = await contactApi.sendContactMessage(data);
      if (response && response.message) {
        enqueueSnackbar(response.message, { variant: 'success' });
        reset();
      } else if (response && response.error) {
        enqueueSnackbar(response.error, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
      reset();
      enqueueSnackbar('An unexpected error occurred.', { variant: 'error' });
    }
  });

  const renderHead = (
    <Stack spacing={1} sx={{ mb: 6, textAlign: { xs: 'center', md: 'left' } }}>
      <m.div variants={varFade().inUp}>
        <Typography variant="h3" sx={{ fontWeight: 700 }}>
          Get in Touch
        </Typography>
      </m.div>
      <m.div variants={varFade().inUp}>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Whether it’s a project, collaboration, or just a hello — I’d love to hear from you!
        </Typography>
      </m.div>
    </Stack>
  );

  const renderForm = (
    <Stack component={MotionViewport} spacing={5}>
      <Stack spacing={3}>
        <m.div variants={varFade().inUp}>
          <RHFTextField name="name" label="Name" />
        </m.div>

        <m.div variants={varFade().inUp}>
          <RHFTextField name="email" label="Email address" />
        </m.div>

        <m.div variants={varFade().inUp}>
          <RHFTextField name="subject" label="subject" />
        </m.div>

        <m.div variants={varFade().inUp}>
          <RHFTextField
            fullWidth
            name="message"
            label="Enter your Comment or message here."
            multiline
            rows={4}
          />
        </m.div>
      </Stack>

      <m.div variants={varFade().inUp}>
        <LoadingButton
          color="primary"
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          sx={{
            px: 5,
            py: 1.8,
            fontWeight: 600,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
          }}
        >
          Send Message
        </LoadingButton>
      </m.div>
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHead}

      {renderForm}
    </FormProvider>
  );
}
